import React, { useEffect, useRef } from "react";
import { App as CapacitorApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { SystemBars, SystemBarsStyle } from "@capacitor/core";
import { Network } from "@capacitor/network";
import { focusManager } from "@tanstack/react-query";
import { useTheme } from "next-themes";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { getNativePlatform, isNativeApp } from "@/lib/runtime";
import { resolveNativeDeepLink } from "@/lib/nativeDeepLinks";
import { syncNativePushRegistration } from "@/lib/nativePushNotifications";
import { useAuth } from "@/contexts/AuthContext";
import queryClient from "@/lib/queryClient";

const NATIVE_ROOT_ROUTES = new Set(["/", "/auth"]);
const NETWORK_TOAST_ID = "native-network-status";

function closeOpenOverlay() {
  const openOverlay = document.querySelector(
    '[data-state="open"][role="dialog"], [data-state="open"][role="alertdialog"], [data-state="open"][role="menu"], [data-state="open"][role="listbox"]'
  );

  if (!openOverlay) return false;

  document.dispatchEvent(new KeyboardEvent("keydown", {
    key: "Escape",
    code: "Escape",
    bubbles: true,
    cancelable: true,
  }));
  return true;
}

export default function NativeRuntimeBridge() {
  const location = useLocation();
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const { user } = useAuth();
  const native = isNativeApp();
  const platform = getNativePlatform();
  const pathnameRef = useRef(location.pathname);
  const navigateRef = useRef(navigate);
  const lastDeepLinkRef = useRef({ destination: "", handledAt: 0 });
  const launchUrlHandledRef = useRef(false);

  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    if (!native) return undefined;

    const runtimeClasses = ["capacitor-native", `capacitor-${platform}`];
    if (platform === "android") runtimeClasses.push("native-ui-v3");

    document.documentElement.classList.add(...runtimeClasses);
    document.body.classList.add(...runtimeClasses);

    return () => {
      document.documentElement.classList.remove(...runtimeClasses);
      document.body.classList.remove(...runtimeClasses);
    };
  }, [native, platform]);

  useEffect(() => {
    if (!native) return undefined;

    let disposed = false;
    let appStateHandle;
    let backButtonHandle;
    let appUrlOpenHandle;

    const openDeepLink = (url) => {
      const normalizedUrl = typeof url === "string" && url.startsWith("/")
        ? `lapstr://app${url}`
        : url;
      const destination = resolveNativeDeepLink(normalizedUrl);
      if (!destination) return;

      const now = Date.now();
      if (
        lastDeepLinkRef.current.destination === destination
        && now - lastDeepLinkRef.current.handledAt < 1000
      ) return;

      lastDeepLinkRef.current = { destination, handledAt: now };
      navigateRef.current(destination);
    };

    const registerListeners = async () => {
      appStateHandle = await CapacitorApp.addListener("appStateChange", ({ isActive }) => {
        focusManager.setFocused(isActive);
      });

      if (platform === "android") {
        backButtonHandle = await CapacitorApp.addListener("backButton", ({ canGoBack }) => {
          if (closeOpenOverlay()) return;

          if (canGoBack) {
            window.history.back();
            return;
          }

          if (!NATIVE_ROOT_ROUTES.has(pathnameRef.current)) {
            navigateRef.current("/", { replace: true });
            return;
          }

          void CapacitorApp.minimizeApp();
        });
      }

      appUrlOpenHandle = await CapacitorApp.addListener("appUrlOpen", ({ url }) => {
        openDeepLink(url);
      });

      if (!launchUrlHandledRef.current) {
        const launchUrl = await CapacitorApp.getLaunchUrl();
        if (!disposed && !launchUrlHandledRef.current) {
          launchUrlHandledRef.current = true;
          if (launchUrl?.url) openDeepLink(launchUrl.url);
        }
      }

      if (disposed) {
        await appStateHandle?.remove();
        await backButtonHandle?.remove();
        await appUrlOpenHandle?.remove();
      }
    };

    const handlePushDeepLink = (event) => {
      openDeepLink(event.detail?.deeplink);
    };

    const handleForegroundPush = (event) => {
      const notification = event.detail?.notification;
      void queryClient.invalidateQueries({ queryKey: ["notifications", "unreadCount"] });
      void queryClient.invalidateQueries({ queryKey: ["notifications", "unseenCount"] });
      void queryClient.invalidateQueries({ queryKey: ["notifications", "inbox"] });
      if (notification?.title || notification?.body) {
        toast(notification.title || "Lapstr", {
          description: notification.body || "",
        });
      }
    };

    window.addEventListener("native:push-deeplink", handlePushDeepLink);
    window.addEventListener("native:push-received", handleForegroundPush);
    void registerListeners();

    return () => {
      disposed = true;
      focusManager.setFocused(undefined);
      void appStateHandle?.remove();
      void backButtonHandle?.remove();
      void appUrlOpenHandle?.remove();
      window.removeEventListener("native:push-deeplink", handlePushDeepLink);
      window.removeEventListener("native:push-received", handleForegroundPush);
    };
  }, [native, platform]);

  useEffect(() => {
    if (!native || !user) return;
    void syncNativePushRegistration().catch((error) => {
      console.error("[PUSH] Kunde inte synka native push-registrering", error);
    });
  }, [native, user]);

  useEffect(() => {
    if (!native) return undefined;

    const handleExternalLink = (event) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const anchor = event.target.closest?.('a[href]');
      if (!anchor || anchor.target !== "_blank") return;

      let url;
      try {
        url = new URL(anchor.href);
      } catch {
        return;
      }

      if (url.protocol !== "http:" && url.protocol !== "https:") return;

      event.preventDefault();
      void Browser.open({
        url: url.toString(),
        toolbarColor: "#121D20",
      });
    };

    document.addEventListener("click", handleExternalLink);
    return () => document.removeEventListener("click", handleExternalLink);
  }, [native]);

  useEffect(() => {
    if (!native) return undefined;

    let disposed = false;
    let networkHandle;
    let previousConnected;

    const applyNetworkStatus = ({ connected }) => {
      if (!connected) {
        toast.error("Ingen internetanslutning", {
          id: NETWORK_TOAST_ID,
          description: "Lapstr ansluter automatiskt igen när nätverket är tillbaka.",
          duration: Infinity,
        });
      } else {
        toast.dismiss(NETWORK_TOAST_ID);
        if (previousConnected === false) {
          toast.success("Anslutningen är återställd", { duration: 2500 });
        }
      }

      previousConnected = connected;
    };

    const registerNetworkListener = async () => {
      const currentStatus = await Network.getStatus();
      if (disposed) return;
      applyNetworkStatus(currentStatus);

      networkHandle = await Network.addListener("networkStatusChange", applyNetworkStatus);
      if (disposed) await networkHandle.remove();
    };

    void registerNetworkListener();

    return () => {
      disposed = true;
      toast.dismiss(NETWORK_TOAST_ID);
      void networkHandle?.remove();
    };
  }, [native]);

  useEffect(() => {
    if (!native) return;

    void SystemBars.show();
    void SystemBars.setStyle({
      style: resolvedTheme === "dark" ? SystemBarsStyle.Dark : SystemBarsStyle.Light,
    });
  }, [native, resolvedTheme]);

  return null;
}
